import axios from 'axios'

// Create axios instance
const api = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL || 'http://localhost:8000/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
})

// Request interceptor - Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = getToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor - Handle errors
api.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    // Handle 401 Unauthorized - Token expired or invalid
    if (error.response?.status === 401) {
      clearToken()
      // Redirect to login if not already there
      if (window.location.pathname !== '/login') {
        window.location.href = '/login'
      }
    }
    return Promise.reject(error)
  }
)

// Token management functions
export const getToken = () => {
  return localStorage.getItem('token') || sessionStorage.getItem('token')
}

export const setToken = (token, remember = false) => {
  if (remember) {
    localStorage.setItem('token', token)
  } else {
    sessionStorage.setItem('token', token)
  }
}

export const clearToken = () => {
  localStorage.removeItem('token')
  sessionStorage.removeItem('token')
}

// Auth API methods
export const authAPI = {
  loginWithKey: (permanentKey) => api.post('/auth/login/key', { permanent_key: permanentKey }),
  sendOTP: (email) => api.post('/auth/otp/send', { email }),
  loginWithOTP: (email, otpCode) => api.post('/auth/login/otp', { email, otp_code: otpCode }),
  // Register with key (method 1): no body, returns token + permanent_key
  registerWithKey: () => api.post('/auth/register/key'),
  // Register with email (method 2): phase 1 = { email }, phase 2 = { email, otp_code }; returns token + permanent_key on phase 2
  register: (userData) => api.post('/auth/register', userData),
  logout: () => api.post('/auth/logout'),
  recoverKey: (email) => api.post('/auth/key/recover', { email }),
  forgotPassword: (email) => api.post('/auth/password/forgot', { email }),
  resetPassword: (email, otpCode, password, passwordConfirmation) => api.post('/auth/password/reset', {
    email,
    otp_code: otpCode,
    password,
    password_confirmation: passwordConfirmation
  }),
  getKey: () => api.get('/auth/key'),
  getProfile: () => api.get('/profile')
}

// Profile API methods
export const profileAPI = {
  get: () => api.get('/profile'),
  update: (profileData) => api.put('/profile', profileData),
  changePassword: (currentPassword, newPassword, newPasswordConfirmation) => api.put('/profile/change-password', {
    current_password: currentPassword,
    new_password: newPassword,
    new_password_confirmation: newPasswordConfirmation
  }),
  addEmail: (email) => api.post('/profile/add-email', { email }),
  verifyEmail: (email, otpCode) => api.post('/profile/verify-email', { email, otp_code: otpCode }),
  // Security: transaction password + 2FA
  getSecurityStatus: () => api.get('/profile/security-status'),
  hasTransactionPassword: () => api.get('/profile/has-transaction-password'),
  setTransactionPassword: (currentVerification, newPassword, newPasswordConfirmation) => api.post('/profile/set-transaction-password', {
    current_verification: currentVerification || undefined,
    new_password: newPassword,
    new_password_confirmation: newPasswordConfirmation
  }),
  setup2FA: (method = 'totp') => api.get('/profile/2fa/setup', { params: { method } }),
  enable2FA: (code, method = 'totp') => api.post('/profile/2fa/enable', { code, method }),
  disable2FA: (verification) => api.post('/profile/2fa/disable', { verification })
}

// Cryptocurrency API methods
export const cryptocurrencyAPI = {
  // Get all active cryptocurrencies
  getAll: () => api.get('/cryptocurrencies'),
  // Get all cryptocurrencies (including inactive)
  getAllIncludingInactive: () => api.get('/cryptocurrencies/all'),
  // Get networks for a specific cryptocurrency
  getNetworks: (cryptocurrencyId) => api.get(`/cryptocurrencies/${cryptocurrencyId}/networks`),
  // Update cryptocurrency prices from CoinMarketCap
  updatePrices: () => api.post('/cryptocurrencies/update-prices'),
  // Update cryptocurrency logos from CoinMarketCap
  updateLogos: () => api.post('/cryptocurrencies/update-logos')
}

// Exchange API methods
export const exchangeAPI = {
  // Get exchange availability and admin-controlled schedule
  getStatus: () => api.get('/exchange/status'),
  // Get admin-defined tradable pairs
  getPairs: () => api.get('/exchange/pairs'),
  // Calculate exchange rate between two cryptocurrencies
  calculate: (fromCryptoId, toCryptoId, amount) => api.post('/exchange/calculate', {
    from_cryptocurrency_id: fromCryptoId,
    to_cryptocurrency_id: toCryptoId,
    amount
  }),
  // Initiate a new exchange transaction
  initiate: (fromCryptoId, toCryptoId, amount) => api.post('/exchange/initiate', {
    from_cryptocurrency_id: fromCryptoId,
    to_cryptocurrency_id: toCryptoId,
    amount
  }),
  // Confirm and complete an exchange transaction
  confirm: (exchangeId, securityCode) => api.post(`/exchange/${exchangeId}/confirm`, {
    security_code: securityCode
  }),
  // Get user exchanges for history / polling
  getAll: (page = 1, perPage = 15) => api.get('/exchange', {
    params: { page, per_page: perPage }
  }),
  getById: (exchangeId) => api.get(`/exchange/${exchangeId}`)
}

// Wallet API methods
export const walletAPI = {
  // Get all wallets for authenticated user
  getAll: () => api.get('/wallets'),
  // Get specific wallet details
  getById: (walletId) => api.get(`/wallets/${walletId}`),
  // Get wallet transactions
  getTransactions: (walletId, page = 1, perPage = 15) => api.get(`/wallets/${walletId}/transactions`, {
    params: { page, per_page: perPage }
  }),
  transferLegacyProfit: (profitWalletId, amount, securityCode) => api.post('/wallets/legacy-profit/transfer-to-main', {
    profit_wallet_id: profitWalletId,
    amount,
    security_code: securityCode
  }),
  transferInternal: (sourceWalletId, destinationWalletId, amount, securityCode) => api.post('/wallets/internal-transfer', {
    source_wallet_id: sourceWalletId,
    destination_wallet_id: destinationWalletId,
    amount,
    security_code: securityCode
  })
}

// Deposit API methods
export const depositAPI = {
  // Get deposit wallet addresses for all networks
  getWalletAddresses: () => api.get('/deposits/wallet-addresses'),
  // Initiate a new deposit
  initiate: (cryptocurrencyId, blockchainNetworkId, amount, transactionHash) => api.post('/deposits/initiate', {
    cryptocurrency_id: cryptocurrencyId,
    blockchain_network_id: blockchainNetworkId,
    amount,
    transaction_hash: transactionHash
  }),
  // Get user deposits
  getAll: (page = 1, perPage = 15) => api.get('/deposits', {
    params: { page, per_page: perPage }
  }),
  // Get specific deposit
  getById: (depositId) => api.get(`/deposits/${depositId}`)
}

// Withdrawal API methods
export const withdrawalAPI = {
  // Request a new withdrawal
  request: (cryptocurrencyId, blockchainNetworkId, amount, destinationAddress) => api.post('/withdrawals/request', {
    cryptocurrency_id: cryptocurrencyId,
    blockchain_network_id: blockchainNetworkId,
    amount,
    destination_address: destinationAddress
  }),
  // Confirm withdrawal with security code
  confirm: (withdrawalId, securityCode) => api.post(`/withdrawals/${withdrawalId}/confirm`, {
    security_code: securityCode
  }),
  // Get user withdrawals
  getAll: (page = 1, perPage = 15) => api.get('/withdrawals', {
    params: { page, per_page: perPage }
  }),
  // Get specific withdrawal
  getById: (withdrawalId) => api.get(`/withdrawals/${withdrawalId}`)
}

// Loan API methods
export const loanAPI = {
  // Loan public settings (multipliers, contract info, etc.)
  getSettings: () => api.get('/loans/settings'),
  // Loan types/tiers (simple list without settings detail)
  getTiers: () => api.get('/loans/tiers'),
  // Get amount tiers for a specific loan type
  getAmountTiers: (loanType) => api.get('/loans/amount-tiers', {
    params: {
      loan_type: loanType
    }
  }),
  // Check eligibility for a loan amount/type with duration
  checkEligibility: (loanType, amount, durationMonths, tierId = null) => api.post('/loans/check-eligibility', {
    loan_type: loanType,
    amount,
    duration_months: durationMonths,
    tier_id: tierId
  }),
  // Request a loan with duration
  request: (loanType, amount, durationMonths, securityCode, tierId = null) => api.post('/loans/request', {
    loan_type: loanType,
    amount,
    duration_months: durationMonths,
    security_code: securityCode,
    tier_id: tierId
  }),
  // Get available loans for deposit usage
  getAvailable: () => api.get('/loans/available'),
  // List my loans (optional filters)
  getMyLoans: (status, perPage = 15) => api.get('/loans/my-loans', {
    params: {
      status,
      per_page: perPage
    }
  }),
  // Loan details
  getById: (id) => api.get(`/loans/${id}`)
}

// Credits API methods
export const creditsAPI = {
  // Get user credits info
  getMyCredits: () => api.get('/credits/my-credits'),
  // Get credit history
  getHistory: (page = 1, perPage = 20) =>
    api.get('/credits/history', { params: { page, per_page: perPage } }),
  // Get available tasks
  getTasks: () => api.get('/credits/tasks')
}

// Home API methods
export const homeAPI = {
  getShortcuts: () => api.get('/home-shortcuts'),
  getCreditLoanProgress: () => api.get('/home/credit-loan-progress')
}

// FAQ API methods
export const faqAPI = {
  // Get all FAQ categories with their questions
  getAllCategories: () => api.get('/faq/categories'),
  // Get FAQs of a specific category
  getCategoryBySlug: (slug) => api.get(`/faq/categories/${slug}`),
  // Get a specific FAQ question
  getQuestionById: (id) => api.get(`/faq/questions/${id}`),
  // Search in FAQ questions and answers
  search: (query) => api.get('/faq/search', { params: { q: query } })
}

// Ticket API methods
export const ticketAPI = {
  // Get all ticket subjects
  getSubjects: () => api.get('/tickets/subjects'),
  // Get all user tickets
  getAll: () => api.get('/tickets'),
  // Create a new ticket
  create: (formData) => api.post('/tickets', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  // Get a specific ticket with all replies
  getById: (ticketId) => api.get(`/tickets/${ticketId}`),
  // Reply to a ticket
  reply: (ticketId, formData) => api.post(`/tickets/${ticketId}/reply`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  // Close a ticket
  close: (ticketId) => api.post(`/tickets/${ticketId}/close`, {}),
  // Reopen a ticket
  reopen: (ticketId) => api.post(`/tickets/${ticketId}/reopen`, {})
}

// Settings API methods
export const settingsAPI = {
  // Get all settings
  getAll: () => api.get('/settings'),
  // Get settings by group (general, about, contact, social)
  getByGroup: (group) => api.get(`/settings/group/${group}`),
  // Get single setting by key
  getByKey: (key) => api.get(`/settings/${key}`)
}

export const appReleaseAPI = {
  getCurrent: () => api.get('/app-release', { timeout: 5000 })
}

// Banner API methods
export const bannerAPI = {
  // Get active banners (optionally filter by type)
  getActive: (type = null) => {
    const params = type ? { type } : {}
    return api.get('/banners', { params })
  },
  // Get banners by specific type
  getByType: (type) => api.get(`/banners/type/${type}`),
  // Track banner view
  trackView: (bannerId) => api.post(`/banners/${bannerId}/view`),
  // Track banner click
  trackClick: (bannerId) => api.post(`/banners/${bannerId}/click`)
}

// Banner Types Constants
export const BANNER_TYPES = {
  MAIN_SLIDER: 'main_slider', // اسلایدر اصلی
  DEPOSIT_BANNER: 'deposit_banner', // بنر سپرده‌گذاری
  DEPOSIT_PROFIT_BANNER: 'deposit_profit_banner', // بنر سود
  DEPOSIT_TIMER_BANNER: 'deposit_timer_banner', // بنر تایمر
  WALLET_HEADER_BANNER: 'wallet_header_banner', // بنر هدر کیف پول
  COURSE_BANNER: 'course_banner', // بنر آموزشی
  STAKING_START_BANNER: 'staking_start_banner', // بنر کوچک شروع سپرده
  STAKING_MY_DEPOSITS_BANNER: 'staking_my_deposits_banner', // بنر کوچک سپرده‌های من
  ABOUT_US_BANNER: 'about_us_banner' // بنر درباره ما
}

// Generic API methods
export const get = (url, config = {}) => api.get(url, config)
export const post = (url, data, config = {}) => api.post(url, data, config)
export const put = (url, data, config = {}) => api.put(url, data, config)
export const patch = (url, data, config = {}) => api.patch(url, data, config)
export const del = (url, config = {}) => api.delete(url, config)

export default api
